import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useMemo } from 'react';

import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { useTRPC } from '#src/trpc.js';

interface PackageStoreLinkProps {
	projectId: string;
	buildId?: string;
	referenceDesignator: string;
	displayText: string;
	toTab: 'datasheet' | 'pinout' | 'functions' | 'parameters' | 'raw' | 'signal-drive-modes';
	className?: string;
}

export function PackageStoreLink({
	projectId,
	buildId,
	referenceDesignator,
	displayText,
	toTab: tabType,
	className,
}: PackageStoreLinkProps) {
	const trpc = useTRPC();
	const { coreModel } = useCoreModelContext();

	const projectModuleReferenceDesignator = useMemo(() => {
		if (!coreModel) {
			return referenceDesignator;
		}

		const foundModule = coreModel.modules.find((m) => m.name === referenceDesignator);
		if (foundModule) {
			return foundModule.name;
		}

		for (const design of Object.values(coreModel.designs)) {
			const module = design.modules.find((m) => m.name === referenceDesignator);
			if (module) {
				const useName = module.fullModuleName ?? module.name;
				const projectModule = coreModel.modules.find((m) => m.name === useName);
				return projectModule?.name ?? referenceDesignator;
			}
		}

		return referenceDesignator;
	}, [coreModel, referenceDesignator]);

	const { data: packageData, isLoading } = useQuery(
		trpc.project.getPackageByRefDesignator.queryOptions(
			{
				projectId,
				buildId,
				referenceDesignator: projectModuleReferenceDesignator,
			},
			{
				throwOnError: false,
			},
		),
	);

	const pkg = packageData?.package;

	if (!displayText) {
		return <span className='text-gray-a8 italic'>No data</span>;
	}

	if (isLoading) {
		return (
			<div className={clsx('flex items-center gap-1', className)}>
				<span>{displayText}</span>
				<CircularProgress size={12} className='ml-1' />
			</div>
		);
	}

	if (!pkg) {
		return <span className={className}>{displayText}</span>;
	}

	// Map tabType to the actual route path
	const tabRoutes = {
		datasheet: '/package-store/$packageFamilyId/packages/$packageId/datasheet',
		pinout: '/package-store/$packageFamilyId/packages/$packageId/pinout',
		functions: '/package-store/$packageFamilyId/packages/$packageId/functions',
		parameters: '/package-store/$packageFamilyId/packages/$packageId/parameters',
		raw: '/package-store/$packageFamilyId/packages/$packageId/raw',
		'signal-drive-modes': '/package-store/$packageFamilyId/packages/$packageId/signal-drive-modes',
	} as const;

	return (
		<Link
			to={tabRoutes[tabType]}
			params={{ packageFamilyId: pkg.pkgFamilyId, packageId: pkg.id }}
			target='_blank'
			rel='noopener noreferrer'
			className={`flex items-center gap-1 underline ${className ?? ''}`}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<div>{displayText}</div>
			<OpenInNewIcon className='flex w-4 items-center' />
		</Link>
	);
}
